import { useId, useState } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Checkbox } from '../atoms/Checkbox';
import { Inline } from '../atoms/Inline/Inline';
import { Input } from '../atoms/Input/Input';
import { Select } from '../atoms/Select/Select';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { Dialog } from '../ui/Dialog/Dialog';
import {
  SENSOR_KINDS,
  SENSOR_LOCATIONS,
  SENSOR_PARAMETER_IDS,
  measurementUnitFits,
  type SensorKind,
  type SensorLocation,
  type SensorMapping,
  type SensorParameterId,
  type UpdateSensorMappingInput,
} from '../../api/controller';
import './Controller.css';

export interface SensorMappingDialogProps {
  mapping: SensorMapping;
  onCancel: () => void;
  onSubmit: (input: UpdateSensorMappingInput) => void;
  isSubmitting: boolean;
  submitError: boolean;
}

/** "10", "0,1", "1.5" → number > 0; null otherwise. */
function parseScale(input: string): number | null {
  const value = Number(input.trim().replace(',', '.'));
  return Number.isFinite(value) && value > 0 && value <= 1_000_000 ? value : null;
}

/** Edit what a reported key means: kind, parameter, where it is measured, label, unit, scale, cost, measurement. */
export function SensorMappingDialog({ mapping, onCancel, onSubmit, isSubmitting, submitError }: SensorMappingDialogProps) {
  const { t } = useTranslation();
  const id = useId();
  const [kind, setKind] = useState<SensorKind>(mapping.kind);
  const [parameterId, setParameterId] = useState<SensorParameterId | ''>(mapping.parameterId ?? '');
  const [location, setLocation] = useState<SensorLocation>(mapping.location);
  const [label, setLabel] = useState(mapping.label ?? '');
  const [unit, setUnit] = useState(mapping.unit !== null && mapping.unit !== mapping.reportedUnit ? mapping.unit : '');
  const [scale, setScale] = useState(String(mapping.scaleFactor));
  const [countsTowardCost, setCountsTowardCost] = useState(mapping.countsTowardCost);
  const [recordAsMeasurement, setRecordAsMeasurement] = useState(mapping.recordAsMeasurement);
  const [touched, setTouched] = useState(false);

  const scaleValue = parseScale(scale);
  const parameterMissing = kind === 'parameter' && parameterId === '';
  const costKind = kind === 'power' || kind === 'energy';
  const tankParameter = kind === 'parameter' && location === 'tank' && parameterId !== '';
  const unitFits = parameterId !== '' && measurementUnitFits(parameterId, unit.trim() || mapping.reportedUnit);
  const canRecord = tankParameter && unitFits;

  const save = () => {
    setTouched(true);
    if (scaleValue === null || parameterMissing || isSubmitting) return;
    onSubmit({
      kind,
      parameterId: kind === 'parameter' && parameterId !== '' ? parameterId : null,
      location,
      label: label.trim() || null,
      unit: unit.trim() || null,
      scaleFactor: scaleValue,
      countsTowardCost: costKind && countsTowardCost,
      recordAsMeasurement: canRecord && recordAsMeasurement,
    });
  };

  return (
    <Dialog isOpen onClose={onCancel} title={t('controller.mapping.title')}>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <Stack gap="4">
          <Text color="muted" className="controller-mapping__key">{mapping.sourceKey}</Text>
          <Select
            id={`${id}-kind`}
            label={t('controller.mapping.kind')}
            value={kind}
            options={SENSOR_KINDS.map((k) => ({ value: k, label: t(`controller.kind.${k}`) }))}
            onChange={(e) => setKind(e.target.value as SensorKind)}
            fullWidth
          />
          {kind === 'parameter' && (
            <Select
              id={`${id}-parameter`}
              label={t('controller.mapping.parameter')}
              value={parameterId}
              placeholder={t('controller.mapping.parameterPlaceholder')}
              options={SENSOR_PARAMETER_IDS.map((p) => ({ value: p, label: t(`controller.parameter.${p}`) }))}
              onChange={(e) => setParameterId(e.target.value as SensorParameterId)}
              error={touched && parameterMissing ? t('controller.mapping.parameterError') : undefined}
              fullWidth
            />
          )}
          {kind === 'parameter' && (
            <Select
              id={`${id}-location`}
              label={t('controller.mapping.location')}
              hint={t('controller.mapping.locationHint')}
              value={location}
              options={SENSOR_LOCATIONS.map((l) => ({ value: l, label: t(`controller.location.${l}`) }))}
              onChange={(e) => setLocation(e.target.value as SensorLocation)}
              fullWidth
            />
          )}
          <Input
            id={`${id}-label`}
            label={t('controller.mapping.label')}
            value={label}
            maxLength={100}
            onChange={(e) => setLabel(e.target.value)}
            fullWidth
          />
          <Input
            id={`${id}-unit`}
            label={t('controller.mapping.unit')}
            helperText={
              mapping.reportedUnit
                ? t('controller.mapping.unitHint', { unit: mapping.reportedUnit })
                : t('controller.mapping.unitHintNone')
            }
            value={unit}
            maxLength={20}
            onChange={(e) => setUnit(e.target.value)}
            fullWidth
          />
          <Input
            id={`${id}-scale`}
            label={t('controller.mapping.scale')}
            helperText={t('controller.mapping.scaleHint')}
            inputMode="decimal"
            value={scale}
            onChange={(e) => setScale(e.target.value)}
            error={touched && scaleValue === null ? t('controller.mapping.scaleError') : undefined}
            fullWidth
          />
          {costKind && (
            <Stack gap="1">
              <Checkbox
                label={t('controller.mapping.countsTowardCost')}
                checked={countsTowardCost}
                onChange={(e) => setCountsTowardCost(e.target.checked)}
              />
              <Text color="muted" size="sm">{t('controller.mapping.countsHint')}</Text>
            </Stack>
          )}
          {tankParameter && (
            <Stack gap="1">
              <Checkbox
                label={t('controller.mapping.recordAsMeasurement')}
                checked={canRecord && recordAsMeasurement}
                disabled={!unitFits}
                aria-describedby={`${id}-record-hint`}
                onChange={(e) => setRecordAsMeasurement(e.target.checked)}
              />
              <Text id={`${id}-record-hint`} color="muted" size="sm">
                {unitFits ? t('controller.mapping.recordHint') : t('controller.mapping.recordUnitHint')}
              </Text>
            </Stack>
          )}
          {submitError && <Alert>{t('controller.mapping.saveError')}</Alert>}
          <Inline justify="end" gap="2">
            <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
              {t('controller.common.cancel')}
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting} aria-busy={isSubmitting}>
              {t('controller.common.save')}
            </Button>
          </Inline>
        </Stack>
      </form>
    </Dialog>
  );
}
